import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { Hourglass } from '~/ui/BrandMark';
import { type CSSProperties, useEffect, useState } from 'react';
import { Toaster as Sonner, type ToasterProps, toast } from 'sonner';
import { type ToastPlace, useToastPlace } from '~/lib/toastPlace';
import { useTheme } from '~/lib/theme';

/*
 * shadcn's Sonner wrapper, fitted to 11Deeds: the theme comes from
 * src/lib/theme.ts (not next-themes) and every colour is one of our tokens,
 * so toasts follow light/dark and the accent preset. Mounted once in
 * __root.tsx; screens never render it and never import `sonner` directly —
 * they call `notify` from src/lib/notify.ts.
 *
 * Where they appear is the person's choice for this device (src/lib/toastPlace.ts).
 * With two or more on screen, a "Clear all" button sits at the edge and the
 * stack moves up (or down) to make room for it.
 */

/** Sonner's own edge distance, desktop and phone. */
const EDGE = 24;
const EDGE_PHONE = 16;
/** Room for the Clear all button. */
const ROOM = 44;

/** How many pop-ups are on screen, counted from sonner's own list. */
function useToastCount(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const countNow = () =>
      setCount(document.querySelectorAll('[data-sonner-toast]:not([data-removed="true"])').length);
    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(countNow);
    });
    const section = document.querySelector('section[aria-label^="Notifications"]') ?? document.body;
    observer.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-removed'],
    });
    countNow();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
  return count;
}

function edgeOffset(place: ToastPlace, edge: number, room: boolean) {
  const y = place.startsWith('top') ? 'top' : 'bottom';
  return { [y]: edge + (room ? ROOM : 0), left: edge, right: edge };
}

function ClearAll({ place }: { place: ToastPlace }) {
  const [y, x] = place.split('-') as ['top' | 'bottom', 'left' | 'center' | 'right'];
  const style: CSSProperties = { [y]: 'var(--clear-all-edge)' };
  if (x === 'left') style.left = 'var(--clear-all-edge)';
  else if (x === 'right') style.right = 'var(--clear-all-edge)';
  else {
    style.left = '50%';
    style.transform = 'translateX(-50%)';
  }
  return (
    <button
      type="button"
      onClick={() => toast.dismiss()}
      style={style}
      className="fixed z-[1000000000] inline-flex h-8 items-center rounded-full border border-line bg-paper px-3.5 text-xs font-medium text-ink-2 shadow-layer [--clear-all-edge:16px] hover:bg-paper-2 hover:text-ink sm:[--clear-all-edge:24px]"
    >
      Clear all
    </button>
  );
}

function Toaster(props: ToasterProps) {
  const [theme] = useTheme();
  const place = useToastPlace();
  const many = useToastCount() >= 2;
  return (
    <>
    {many && <ClearAll place={place} />}
    <Sonner
      theme={theme}
      position={place}
      offset={edgeOffset(place, EDGE, many)}
      mobileOffset={edgeOffset(place, EDGE_PHONE, many)}
      richColors
      closeButton
      className="toaster group"
      icons={{
        success: <CircleCheck size={18} aria-hidden />,
        info: <Info size={18} aria-hidden />,
        warning: <TriangleAlert size={18} aria-hidden />,
        error: <CircleAlert size={18} aria-hidden />,
        loading: <Hourglass size={18} />,
      }}
      toastOptions={{
        classNames: {
          toast: 'rounded-panel! shadow-layer! font-sans! gap-2.5!',
          title: 'font-medium!',
          description: 'opacity-90!',
        },
      }}
      style={
        {
          fontFamily: 'var(--font-sans)',
          '--border-radius': 'var(--radius-panel)',
          '--normal-bg': 'var(--paper)',
          '--normal-text': 'var(--ink)',
          '--normal-border': 'var(--line)',
          '--success-bg': 'var(--done-soft)',
          '--success-text': 'var(--done)',
          '--success-border': 'color-mix(in srgb, var(--done) 40%, transparent)',
          '--error-bg': 'var(--late-soft)',
          '--error-text': 'var(--late)',
          '--error-border': 'color-mix(in srgb, var(--late) 40%, transparent)',
          '--warning-bg': 'var(--warn-soft)',
          '--warning-text': 'var(--warn)',
          '--warning-border': 'color-mix(in srgb, var(--warn) 40%, transparent)',
          '--info-bg': 'var(--accent-soft)',
          '--info-text': 'var(--accent-ink)',
          '--info-border': 'color-mix(in srgb, var(--accent) 40%, transparent)',
        } as CSSProperties
      }
      {...props}
    />
    </>
  );
}

export { Toaster };
